import { cn } from "@/lib/cn";
import { ButtonHTMLAttributes, forwardRef } from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-white border-border-thick shadow-brutal-sm hover:bg-primary/90 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none",
  secondary:
    "bg-safety-orange text-white border-border-thick shadow-brutal-sm hover:bg-safety-orange/90 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none",
  ghost:
    "bg-white text-border-thick border-border-thick/20 shadow-none hover:bg-retro-gray/30 hover:border-border-thick/40",
  destructive:
    "bg-danger text-white border-border-thick shadow-brutal-sm hover:bg-danger/90 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-sm rounded-md",
  md: "px-5 py-2.5 text-sm rounded-lg",
  lg: "px-6 py-3 text-base rounded-lg",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          "inline-flex items-center justify-center gap-1.5 font-display font-semibold border-2",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
          "transition-all duration-100 cursor-pointer",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:active:translate-x-0 disabled:active:translate-y-0 disabled:active:shadow-brutal-sm",
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";
